import { CheckCircle2, ChevronDown, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/home/primitives'

// Form controls on design system v2. Plain elements, so they work in any form.
const CONTROL =
  'block w-full rounded-control border border-rule-strong bg-surface px-3 text-[15px] text-ink placeholder:text-graphite/60 transition-colors focus:border-signal focus:outline-none focus:ring-2 focus:ring-signal/20 disabled:opacity-60'

export function Field({ id, label, required, hint, className, children }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span aria-hidden className="text-signal">
            {' '}
            *
          </span>
        )}
      </label>
      {children}
      {hint && <p className="text-xs text-graphite">{hint}</p>}
    </div>
  )
}

export function TextInput({ className, ...props }) {
  return <input {...props} className={cn(CONTROL, 'h-11', className)} />
}

export function TextArea({ className, ...props }) {
  return (
    <textarea
      {...props}
      className={cn(CONTROL, 'min-h-[128px] py-2.5 leading-relaxed', className)}
    />
  )
}

export function SelectInput({ className, children, ...props }) {
  return (
    <div className="relative">
      <select
        {...props}
        className={cn(CONTROL, 'h-11 appearance-none pr-9', className)}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-graphite"
      />
    </div>
  )
}

export function SubmitButton({ loading, className, children, ...props }) {
  return (
    <button
      type="submit"
      disabled={loading}
      aria-busy={loading || undefined}
      className={buttonClasses({
        className: cn('w-full disabled:opacity-70', className),
      })}
      {...props}
    >
      {loading ? (
        <Loader2 aria-label="Wird gesendet" className="h-4 w-4 animate-spin" />
      ) : (
        children
      )}
    </button>
  )
}

export function FormError({ children }) {
  return (
    <p
      role="alert"
      className="rounded-control border border-danger/20 bg-danger-soft px-3 py-2 text-sm text-danger"
    >
      {children}
    </p>
  )
}

// Replaces a form after a successful submit.
export function FormSuccess({ title, children, action }) {
  return (
    <div
      role="status"
      className="flex min-h-[420px] flex-col items-center justify-center gap-4 text-center"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-success">
        <CheckCircle2 aria-hidden className="h-6 w-6" />
      </span>
      <p className="text-2xl font-[450] text-ink [font-stretch:110%]">
        {title}
      </p>
      <p className="max-w-sm text-[15px] leading-relaxed text-graphite">
        {children}
      </p>
      {action}
    </div>
  )
}
